import {
  addDays,
  formatClock,
  formatEntryDuration,
  formatTimeOfDay,
  localDate,
  localMinutes,
  missingFieldsMessage,
  missingRequiredFields,
  PROJECT_REQUIRED_MESSAGE,
  parseDuration,
  parseTimeOfDay,
  type TimeEntryPatch,
  zonedTimeToUtc,
} from '@taim/shared';
import { Clock, List, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useProjects, useTagScope } from '@/features/catalog/hooks';
import type { ProjectPickerValue } from '@/features/catalog/ProjectPicker';
import { useStableCallback } from '@/hooks/useStableCallback';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import {
  useCalendar,
  useCreateEntry,
  useDeleteEntry,
  useNow,
  useRunningEntry,
  useStartTimer,
  useStopTimer,
  useToday,
  useUpdateEntry,
} from './hooks';
import { BillableToggle, CommitInput, DatePicker, errorMessage } from './inputs';
import { ProjectPicker, TagPicker } from './pickers';

type Mode = 'timer' | 'manual';
const MODE_KEY = 'taim.tracker.mode';
const DESCRIPTION_DEBOUNCE_MS = 600;
/** Stopping an entry longer than this shows a warning (a timer left running by mistake). */
const LONG_ENTRY_SEC = 24 * 3600;

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'manual' ? 'manual' : 'timer';
  } catch {
    return 'timer';
  }
}

function writeMode(mode: Mode) {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Storage unavailable (private mode): the choice just isn't remembered.
  }
}

type Draft = { project: ProjectPickerValue | null; tagIds: string[]; billable: boolean | null };
const emptyDraft: Draft = { project: null, tagIds: [], billable: null };

/**
 * The timer's clock, plus the browser tab title while it runs ("0:12:34 · Project — Taim"). It
 * ticks on its own, so the rest of the bar (pickers, open popovers) doesn't re-render every second.
 */
function TimerClock({ startAt, label }: { startAt: string | null; label: string }) {
  const now = useNow(startAt !== null);
  const elapsed = startAt ? Math.max(0, Math.floor((now - Date.parse(startAt)) / 1000)) : 0;
  useEffect(() => {
    if (startAt) document.title = `${formatEntryDuration(elapsed)}${label ? ` · ${label}` : ''} — Taim`;
  }, [startAt, elapsed, label]);
  return <span className="w-24 text-center font-medium text-lg tabular-nums">{formatClock(elapsed)}</span>;
}

/**
 * The Time Tracker's top bar: "What are you working on?", project, tags, billable and either the
 * running clock with START/STOP (timer mode) or start/end/date/duration with ADD (manual mode).
 */
export function TimerBar() {
  const { timeZone, weekStart } = useCalendar();
  const { tenant } = useAuth();
  const running = useRunningEntry();
  const entry = running.data ?? null;
  const projects = useProjects({ archived: 'all' });
  const tagScope = useTagScope();
  const start = useStartTimer();
  const stop = useStopTimer();
  const update = useUpdateEntry();
  const discard = useDeleteEntry();
  const create = useCreateEntry();

  const [chosenMode, setModeState] = useState<Mode>(readMode);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [description, setDescription] = useState('');
  const pendingDescription = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set by START/ADD with required fields missing: they show in red until filled in.
  const [checkRequired, setCheckRequired] = useState(false);

  const isRunning = entry !== null;
  // Like Clockify, a running timer always shows as the timer (clock and STOP), whatever mode was
  // last used; starting one (e.g. ▶ Continue on an entry) switches the bar back to timer mode.
  const mode: Mode = isRunning ? 'timer' : chosenMode;
  useEffect(() => {
    if (!isRunning) return;
    setModeState('timer');
    writeMode('timer');
  }, [isRunning]);

  // Show the running entry's description; clear the field when the timer stops.
  const entryId = entry?.id ?? null;
  const entryDescription = entry?.description ?? '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: only resync when the running entry changes
  useEffect(() => {
    setDescription(entryDescription);
  }, [entryId]);

  // Memoized (like the callbacks below) so the memoized pickers skip re-rendering while typing.
  const entryProjectId = entry?.projectId ?? null;
  const entryTaskId = entry?.taskId ?? null;
  const projectValue = useMemo<ProjectPickerValue | null>(
    () =>
      isRunning
        ? entryProjectId
          ? { projectId: entryProjectId, taskId: entryTaskId }
          : null
        : draft.project,
    [isRunning, entryProjectId, entryTaskId, draft.project],
  );
  const tagIds = entry ? entry.tagIds : draft.tagIds;
  const project = projects.data?.find((p) => p.id === projectValue?.projectId);
  const billable = entry ? entry.billable : (draft.billable ?? project?.billableDefault ?? false);

  // What new time still needs (Clockify's required fields): a project when the workspace requires
  // one, and what its client requires. A running timer is checked by the server when it stops.
  const projectMissing = tenant.requireProject && !projectValue;
  const clientMissing = entry ? [] : missingRequiredFields(project ?? null, { description, tagIds });
  const missing = entry ? [] : [...(projectMissing ? (['project'] as const) : []), ...clientMissing];
  const showMissing = checkRequired && missing.length > 0;
  const invalid = (field: (typeof missing)[number]) => (showMissing && missing.includes(field)) || undefined;
  const requiredMessage = showMissing
    ? [
        projectMissing && PROJECT_REQUIRED_MESSAGE,
        clientMissing.length > 0 && missingFieldsMessage(clientMissing),
      ]
        .filter(Boolean)
        .join(' ')
    : null;
  /** Holds back START/ADD while required fields are missing, highlighting them instead. */
  const blockedByRequired = () => {
    if (missing.length === 0) return false;
    setError(null);
    setCheckRequired(true);
    return true;
  };

  // Browser tab title: the running clock (TimerClock) sets it while a timer runs.
  useEffect(() => {
    if (!isRunning) document.title = 'Taim';
  }, [isRunning]);
  useEffect(
    () => () => {
      document.title = 'Taim';
    },
    [],
  );

  const setMode = (next: Mode) => {
    setModeState(next);
    writeMode(next);
  };

  const onError = (e: unknown) => setError(errorMessage(e));
  const patchRunning = (patch: TimeEntryPatch) => {
    if (!entry) return;
    setError(null);
    update.mutate({ id: entry.id, ...patch }, { onError });
  };

  const flushDescription = () => {
    if (pendingDescription.current) clearTimeout(pendingDescription.current);
    pendingDescription.current = null;
  };
  useEffect(
    () => () => {
      if (pendingDescription.current) clearTimeout(pendingDescription.current);
    },
    [],
  );

  const onDescriptionChange = (text: string) => {
    setDescription(text);
    if (!entry) return;
    flushDescription();
    const id = entry.id;
    pendingDescription.current = setTimeout(() => {
      pendingDescription.current = null;
      update.mutate({ id, description: text }, { onError });
    }, DESCRIPTION_DEBOUNCE_MS);
  };

  const onProject = useStableCallback((value: ProjectPickerValue | null) => {
    if (entry) patchRunning(tagScope.projectPatch(value, entry.tagIds));
    else {
      setDraft((d) => ({
        ...d,
        project: value,
        billable: null,
        tagIds: tagScope.tagsFor(d.tagIds, tagScope.clientOf(value?.projectId)),
      }));
    }
  });
  const onTags = useStableCallback((ids: string[]) => {
    if (entry) patchRunning({ tagIds: ids });
    else setDraft((d) => ({ ...d, tagIds: ids }));
  });
  const onBillable = (value: boolean) => {
    if (entry) patchRunning({ billable: value });
    else setDraft((d) => ({ ...d, billable: value }));
  };

  const details = () => ({
    description: description.trim(),
    projectId: projectValue?.projectId ?? null,
    taskId: projectValue?.taskId ?? null,
    tagIds,
    billable,
  });

  const startTimer = () => {
    if (blockedByRequired()) return;
    setError(null);
    start.mutate(details(), {
      onSuccess: () => {
        setDraft(emptyDraft);
        setCheckRequired(false);
      },
      onError,
    });
  };

  const stopTimer = async () => {
    if (!entry) return;
    setError(null);
    const typed = pendingDescription.current !== null && description !== entry.description;
    flushDescription();
    try {
      if (typed) await update.mutateAsync({ id: entry.id, description });
      const stopped = await stop.mutateAsync({});
      setDescription('');
      if ((stopped.durationSec ?? 0) > LONG_ENTRY_SEC) {
        toast.warning('This entry is longer than 24 hours — check the time');
      }
    } catch (e) {
      onError(e);
    }
  };

  const discardTimer = () => {
    if (!entry) return;
    flushDescription();
    discard.mutate(entry.id, { onSuccess: () => setDescription(''), onError });
  };

  // --- manual mode -------------------------------------------------------------------------
  // Start is a local time on `manualDate`; the duration is real elapsed time, so the end (and the
  // duration shown after editing times) stays right on DST days.
  const today = useToday(timeZone);
  const [manualDate, setManualDateState] = useState(today);
  const [startMin, setStartMin] = useState(() => localMinutes(Date.now(), timeZone));
  const [durationSec, setDurationSec] = useState(0);
  const startAtOn = (date: string, minutes: number) => zonedTimeToUtc(date, minutes, timeZone).getTime();
  const manualStartMs = startAtOn(manualDate, startMin);
  const endMin = localMinutes(manualStartMs + durationSec * 1000, timeZone);
  /** Seconds from `startMs` to the next `minutes` local time on its day, or the day after. */
  const secondsUntil = (date: string, startMs: number, minutes: number) => {
    let endMs = startAtOn(date, minutes);
    if (endMs < startMs) endMs = startAtOn(addDays(date, 1), minutes);
    return Math.round((endMs - startMs) / 1000);
  };
  const setManualDate = (date: string) => {
    // Keep the local start and end times on the new date (entries up to a day long).
    if (durationSec < 24 * 3600) {
      const endDay = localDate(manualStartMs + durationSec * 1000, timeZone);
      if (endDay === manualDate || endDay === addDays(manualDate, 1)) {
        setDurationSec(secondsUntil(date, startAtOn(date, startMin), endMin));
      }
    }
    setManualDateState(date);
  };

  const addManual = () => {
    if (blockedByRequired()) return;
    setError(null);
    const startAt = new Date(manualStartMs);
    const endAt = new Date(manualStartMs + durationSec * 1000);
    create.mutate(
      { ...details(), startAt: startAt.toISOString(), endAt: endAt.toISOString() },
      {
        onSuccess: () => {
          setDraft(emptyDraft);
          setDescription('');
          setCheckRequired(false);
        },
        onError,
      },
    );
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'manual') addManual();
    else if (!entry) startTimer();
  };

  const dateLabel =
    manualDate === today ? 'Today' : manualDate === addDays(today, -1) ? 'Yesterday' : manualDate;

  return (
    <div className="mb-6">
      <form
        aria-label="Timer"
        onSubmit={onSubmit}
        className="flex flex-wrap items-center gap-1 rounded-sm border bg-card px-2 py-1.5 shadow-xs"
      >
        <Input
          placeholder="What are you working on?"
          aria-label="What are you working on?"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          aria-invalid={invalid('description')}
          className={cn(
            'h-10 min-w-48 flex-1 border-0 text-base shadow-none focus-visible:ring-0',
            invalid('description') && 'border',
          )}
        />
        <ProjectPicker
          value={projectValue}
          onChange={onProject}
          allowNone={!tenant.requireProject}
          invalid={invalid('project')}
          className="max-w-64"
        />
        <div className="h-6 border-l border-dashed" />
        <TagPicker
          value={tagIds}
          clientId={tagScope.clientOf(projectValue?.projectId)}
          onChange={onTags}
          invalid={invalid('tags')}
        />
        <div className="h-6 border-l border-dashed" />
        <BillableToggle value={billable} onChange={onBillable} />
        <div className="h-6 border-l border-dashed" />

        {mode === 'timer' ? (
          <>
            <TimerClock
              startAt={entry?.startAt ?? null}
              label={entry ? (project?.name ?? entry.description) : ''}
            />
            {isRunning ? (
              <Button
                type="button"
                variant="destructive"
                className="w-24"
                onClick={stopTimer}
                disabled={stop.isPending}
              >
                STOP
              </Button>
            ) : (
              <Button type="submit" className="w-24" disabled={start.isPending || running.isPending}>
                START
              </Button>
            )}
          </>
        ) : (
          <>
            <CommitInput
              aria-label="Start time"
              className="h-9 w-24 text-center"
              display={formatTimeOfDay(startMin)}
              onCommit={(text) => {
                const m = parseTimeOfDay(text);
                if (m === null) return false;
                // Changing the start keeps the end time (Clockify).
                setDurationSec(secondsUntil(manualDate, startAtOn(manualDate, m), endMin));
                setStartMin(m);
                return formatTimeOfDay(m);
              }}
            />
            <span className="text-muted-foreground">-</span>
            <CommitInput
              aria-label="End time"
              className="h-9 w-24 text-center"
              display={formatTimeOfDay(endMin)}
              onCommit={(text) => {
                const m = parseTimeOfDay(text);
                if (m === null) return false;
                setDurationSec(secondsUntil(manualDate, manualStartMs, m));
                return formatTimeOfDay(m);
              }}
            />
            <DatePicker value={manualDate} onChange={setManualDate} label={dateLabel} weekStart={weekStart} />
            <CommitInput
              aria-label="Duration"
              className="h-9 w-24 text-center font-medium"
              display={formatEntryDuration(durationSec)}
              onCommit={(text) => {
                const sec = parseDuration(text);
                if (sec === null) return false;
                setDurationSec(sec);
                return formatEntryDuration(sec);
              }}
            />
            <Button type="submit" className="w-20" disabled={create.isPending}>
              ADD
            </Button>
          </>
        )}

        <div className="flex flex-col">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label="Timer mode"
            aria-pressed={mode === 'timer'}
            onClick={() => setMode('timer')}
            className={cn(mode === 'timer' ? 'text-primary' : 'text-muted-foreground')}
          >
            <Clock />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label="Manual mode"
            aria-pressed={mode === 'manual'}
            disabled={isRunning}
            title={isRunning ? 'Stop the timer to add time manually' : undefined}
            onClick={() => setMode('manual')}
            className={cn(mode === 'manual' ? 'text-primary' : 'text-muted-foreground')}
          >
            <List />
          </Button>
        </div>
        {isRunning && mode === 'timer' && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Discard timer"
            title="Discard"
            onClick={discardTimer}
            className="text-muted-foreground"
          >
            <Trash2 />
          </Button>
        )}
      </form>
      {(requiredMessage ?? error) && (
        <p role="alert" className="mt-2 text-destructive text-sm">
          {requiredMessage ?? error}
        </p>
      )}
    </div>
  );
}
